/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import d3Tip from 'd3-tip';
import bnd3Service from '../../../services/bnd3.service';
import { isShowingSideMenu } from '../../../services/utils/isShowingSideMenu';
import utilityService from '../../../services/utility.service';
import ChartConst from '../../../constants/chart.constant';
import AppConst from '../../../constants/app.constant';
import MapConst from '../../../constants/map.constant';
import bnChartDataProcessService from '../../../services/chartDataProcess.service';
import mapLeafService from '../../../views/Leafletmapview/Leafletmapview.service';
import d3 from '../../../globalexports/d3.export';
import { routerReplaceInterface } from '../../../services/router.interface';
import {
  roundOfNumber,
} from '../../../services/utils/roundOfNumber';

d3.tip = d3Tip;

function gridService() {
  const content = {};
  let travelRouteSelectorMap = [];
  let travelRouteSelectorMapTextWidths = [];
  const tip = d3Tip().attr('id', 'd3tipcontent').attr('class', 'd3-tip leaflet-popup-content');
  content.drawTravelRouteSoft = function (
    chartObj,
    travelRoute,
    travelAxis,
    squareHeight,
    dateFormat,
  ) {
    const line1 = d3.line()
      .defined((d) => d.arrival_time !== null)
      .curve(d3.curveLinear)
      .x((d) => (travelAxis(d.arrival_time)))
      .y((...args) => {
        const i = args[1];
        const yPoint = (i * squareHeight) + (squareHeight / 2);
        return yPoint;
      });
    chartObj.select('#travelline').attr('d', line1(travelRoute));
    let i = 0;
    let d;
    let textValue;
    let xValue;
    while (i < travelRoute.length) {
      d = travelRoute[i];
      textValue = '';
      if (i === 0) {
        textValue += 'ETD: ';
      } else if (i === travelRoute.length - 1) {
        textValue += 'ETA: ';
      }
      xValue = travelAxis(d.arrival_time);
      travelRouteSelectorMap[i].circleNode.attr('cx', travelAxis(travelRoute[i].arrival_time));
      travelRouteSelectorMap[i].textNode.text(textValue + dateFormat(d.arrival_time));
      travelRouteSelectorMap[i].textNode.attr('x', xValue - (travelRouteSelectorMapTextWidths[i]
        / 2));
      i += 1;
    }
  };
  content.drawTravelRoute = function (
    chartObj,
    travelRoute,
    travelAxis,
    squareHeight,
    dateFormat,
    normalTextHeight,
  ) {
    const line1 = d3.line()
      .defined((d) => d.arrival_time !== null)
      .curve(d3.curveLinear)
      .x((d) => (travelAxis(d.arrival_time)))
      .y((...args) => {
        const i = args[1];
        const yPoint = (i * squareHeight) + (squareHeight / 2);
        return yPoint;
      });
    travelRouteSelectorMapTextWidths = [];
    const travelLineCont = chartObj.append('g').attr('id', 'travelLineCont');
    travelLineCont.append('path').attr('d', line1(travelRoute))
      .attr('id', 'travelline')
      .style('fill', 'none')
      .style('pointer-events', 'none')
      .style('stroke-width', MapConst.geoDiscStyle.strokeWeight)
      .style('stroke', ChartConst.gridRouteLineColor);

    travelLineCont.selectAll('.route-circle').data(travelRoute)
      .enter()
      .append('circle')
      .attr('class', 'travel-point')
      .attr('id', (...args) => `travel-point-${args[1]}`)
      .style('pointer-events', 'none')
      .attr('r', ChartConst.gridRouteDotRadius - 2)
      .attr('fill', ChartConst.gridRouteLineColor)
      .attr('cx', (d) => roundOfNumber(travelAxis(d.arrival_time)))
      .attr('cy', (...args) => {
        const i = args[1];
        return (i * squareHeight) + (squareHeight / 2);
      });
    travelLineCont.selectAll('.route-circle-eta').data(travelRoute)
      .enter()
      .append('text')
      .attr('class', 'shadow-text-grid')
      .attr('id', (...args) => `travel-point-text${args[1]}`)
      .text((d, key) => {
        let textValue = '';
        if (key === 0) {
          textValue += 'ETD: ';
        } else if (key === travelRoute.length - 1) {
          textValue += 'ETA: ';
        }
        return textValue + dateFormat(d.arrival_time);
      })
      .attr('fill', '#fff')
      .attr('y', (...args) => {
        const i = args[1];
        const yValue = (i * squareHeight) + (squareHeight / 2);
        return roundOfNumber(yValue - ((normalTextHeight / 2) + 2));
      })
      .attr('x', (...args) => {
        const xValue = travelAxis(args[0].arrival_time);
        const selfEle = utilityService.getSelfElement(args);
        const textWidth = bnd3Service.getSelectionWidth(d3.select(selfEle));
        travelRouteSelectorMapTextWidths.push(textWidth);
        return roundOfNumber(xValue - (textWidth / 2));
      });

    function loadMap() {
      let i = 0;
      while (i <= travelRoute.length) {
        travelRouteSelectorMap.push({
          textNode: d3.select(`#travel-point-text${i}`),
          circleNode: d3.select(`#travel-point-${i}`),
        });
        i += 1;
      }
    }
    travelRouteSelectorMap = [];
    loadMap();
  };
  // var travelLine = d3.tip().attr("id", "d3tipcontent")
  // .attr("class", "d3-tip leaflet-popup-content");
  content.drawChart = function (
    sourceData,
    chartData,
    chartObj,
    settings,
    airportSqaureWidth,
    squareWidth,
    squareHeight,
    fullData,
    width,
    travelAxis,
    settingsObj,
  ) {
    const normalTextHeight = bnd3Service.getSelectionHeight(d3.select('#bntextrefgrid'));
    const n = 0;
    let dateFormat;
    if (!sourceData) {
      return null;
    }
    const data = utilityService.clone(sourceData[0].forecast);

    function setDateFormat() {
      if (settingsObj.timeZone === AppConst.timeZones.local.key) {
        dateFormat = d3.timeFormat(ChartConst.xAxis.formatLocal + ChartConst.GraphConfig.xAxis
          .timePostFix.local);
      } else {
        dateFormat = d3.utcFormat(ChartConst.xAxis.format + ChartConst.GraphConfig.xAxis
          .timePostFix.zulu);
      }
    }
    setDateFormat();
    tip.html((d) => {
      if (d.colIndex === 0 || !d.forecast_ts_local) {
        return '';
      }
      if (utilityService.isCordovaApplication()) {
        // eslint-disable-next-line no-restricted-globals
        event.stopPropagation();
        // Emitter.emit(EventConst.tapped_outside);
      }
      if (d.tmp_fahrenheit === null || d.tmp_fahrenheit === undefined) {
        d.tmp_fahrenheit = d.tmp;
      }
      if (d.tmp_celsius === null || d.tmp_celsius === undefined) {
        d.tmp_celsius = utilityService.fahrenheitToCelsius(d.tmp);
      }
      if (settingsObj.temperatureUnit === AppConst.temperatureUnits.fahrenheit.key) {
        d.tmp_display_value = d.tmp_fahrenheit;
      } else {
        d.tmp_display_value = d.tmp_celsius;
      }
      if (!d.sunMoonPositionData) {
        d.sunMoonPositionData = utilityService.getSunMoonCalculation(d.forecast_ts_local, d
          .lat, d.lng);
      }
      bnChartDataProcessService.appendWeatherStripData(d, 'forecast_ts_local');
      return mapLeafService.getPopoverHtml(d, settings, true, !(d.direction === 'n'));
    }).direction((d) => d.direction);

    const gridRows = chartObj.selectAll('.row')
      .data(chartData)
      .enter().append('g')
      .attr('class', (ele, i) => `row row-${i + 1}`)
      .attr('id', (d) => `gird-row-${d.icao_id}`)
      .attr('airport-index', (ele, i) => i);
    const gridColumn = gridRows.selectAll(`${'rect .row-'}${n + 1}`)
      .data((d) => d.forecast)
      .enter();
    const gridColumnRect = gridColumn.append('rect')
      .attr('class', (ele, i) => `${'square col-'}${i + 1}`)
      .attr('id', (d, k) => `col-${d.icao_id}-${k}`)
      .attr('width', (ele, i) => (i === 0
        ? airportSqaureWidth
        : squareWidth))
      .attr('height', squareHeight)
      .attr('x', (ele, i) => (i === 0
        ? 0
        : roundOfNumber((i * squareWidth) + (airportSqaureWidth - squareWidth))))
      .attr('y', (ele, i, k) => {
        const airportIndex = d3.select(k[i].parentNode).attr('airport-index');
        return airportIndex * squareHeight;
      })
      .attr('fill', (d, i) => (i === 0
        ? ChartConst.gridView.airportRectColor
        : bnd3Service.getFlightDotColor(d.frl)))
      .attr('stroke', ChartConst.gridView.borderColor)
      .attr('stroke-width', ChartConst.gridView.borderWidth);

    content.showWeatherImage = function (p) {
      return p.frl !== 3 || (p.weatherRepresentation && p.weatherRepresentation
        .levelOfWeather <= ChartConst.gridLevelOfWeatherThreshold);
    };

    /* Invoke the tip in the context of your visualization */
    function tooltipPositionHandle(d3Event, forecast) {
      const d = forecast;
      tip.hide(d);
      const colIndex = d3Event.target.id.split('-')[2];
      const selfEle = d3Event.target;// utilityService.getSelfElement(args);
      // debugger
      const rowIndex = parseFloat(d3.select(selfEle.parentNode).attr('airport-index'));
      let direction;
      if (rowIndex >= (sourceData.length / 2)) {
        direction = 'n';
      } else if (rowIndex < (sourceData.length / 2)) {
        direction = 's';
      }
      d.colIndex = colIndex;
      d.direction = direction;
      tip.show(d, this);
      const defaultTooltipBubbleLeft = ChartConst.gridTooltipDefaultLeft;

      const left = parseFloat(d3.select('#d3tipcontent').style('left'));
      let leftMinOffset = 0;
      let rightMaxOffset = width;
      let leftBubble = 0;
      width += 0;
      leftBubble = left;
      if (isShowingSideMenu()) {
        leftMinOffset = AppConst.sideMenuWidth;
        rightMaxOffset += AppConst.sideMenuWidth;
        leftBubble = -(AppConst.sideMenuWidth - left);
      }
      if (left <= leftMinOffset) {
        d3.select('#gridtipbubble').style(
          'left',
          `${ChartConst.gridTooltipDefaultLeft + leftBubble}px`,
        );
        d3.select('#d3tipcontent').style('left', `${leftMinOffset}px`);
        if (colIndex === 1 && utilityService.isCordovaApplication()) {
          if (direction === 'n') {
            d3.select('#d3tipcontent .conditions').classed('border-bottom-left-radius-0', true);
          } else {
            d3.select('#d3tipcontent .conditions').classed('border-top-left-radius-0', true);
          }
        }
      } else if ((left + ChartConst.gridTooltipContentWidth) > rightMaxOffset) {
        d3.select('#gridtipbubble').style(
          'left',
          `${defaultTooltipBubbleLeft + Math.abs(ChartConst.gridTooltipContentWidth - (rightMaxOffset - left))}px`,
        );
        d3.select('#d3tipcontent').style(
          'left',
          `${left - (ChartConst.gridTooltipContentWidth - (rightMaxOffset - left))}px`,
        );
        if (colIndex === data.length - 1 && utilityService.isCordovaApplication()) {
          if (direction === 'n') {
            d3.select('#d3tipcontent .conditions').classed('border-bottom-right-radius-0', true);
          } else {
            d3.select('#d3tipcontent .conditions').classed('border-top-right-radius-0', true);
          }
        }
      } else {
        d3.select('#gridtipbubble').style('left', `${defaultTooltipBubbleLeft}px`);
      }
      d3.select('#d3tiprootelementgrid').style('visibility', 'visible');
      if (d3Event) {
        d3Event.stopPropagation();
      } else if (window.event) {
        // eslint-disable-next-line no-restricted-globals
        event.stopPropagation();
      }
    }

    chartObj.call(tip);
    if (utilityService.isCordovaApplication()) {
      gridColumnRect.on('click', tooltipPositionHandle);
    } else {
      gridColumnRect.on('mouseover', tooltipPositionHandle);
      gridColumnRect.on('click', tooltipPositionHandle);
      gridColumnRect.on('mouseout', tip.hide);
    }
    chartObj.selectAll('text.label')
      .data(sourceData)
      .enter()
      .append('a')
      .on('click', (d) => {
        routerReplaceInterface({
          name: 'app.dashboard',
          query: {
            start: d.icao_id,
            product: settings.product,
            refresh: true,
          },
        });
      })
      .attr('class', 'airport-stop-id-link')
      .append('text')
      .attr('fill', ChartConst.gridView.airportTextColor)
      .text((d) => d.icao_id)
      .attr('y', (ele, i) => (i * squareHeight) + normalTextHeight + ((squareHeight
        - normalTextHeight) / 2))
      .attr('x', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        const textWidth = bnd3Service.getSelectionWidth(d3.select(selfEle));
        if (textWidth > 0) {
          return (airportSqaureWidth - textWidth) / 2;
        }
        return 0;
      });
    // console.log(settings.timeZone);
    const {
      travelRoute,
    } = fullData;
    // var travelAxis = d3.scaleUtc().range([0, width - airportSqaureWidth]);
    travelAxis.domain(d3.extent(data, (d) => d.forecast_ts));

    const returnObj = [chartObj, travelRoute, travelAxis, squareHeight, dateFormat,
      normalTextHeight,
    ];
    // eslint-disable-next-line prefer-rest-params
    content.drawTravelRoute.apply(utilityService.getSelfElement(arguments), returnObj);
    return returnObj;
  };
  return content;
}
export default gridService();
